/**
 * ContextBuilder 工厂
 * 提供便捷的方法创建配置好的 ContextBuilder 实例
 */

import { ContextBuilder } from './context-builder';
import { ContextConfig } from './types';
import { KnowledgeBase } from '../knowledge-base';
import { WorkflowManager } from '../workflow-manager';
import { TaskAnalyzer } from '../task-analyzer';
import { MemoryIntegrationConfig } from './memory-integration';
import { logger } from '../../utils/logger';

/**
 * 创建 ContextBuilder 的选项
 */
export interface CreateContextBuilderOptions {
  contextConfig?: ContextConfig;
  memoryConfig?: MemoryIntegrationConfig;
  enableMemory?: boolean;
}

/**
 * 从 @gongfu/memory 包导入的适配器（如果可用）
 */
async function tryLoadMemoryAdapters(): Promise<{
  client?: any;
  taskAdapter?: any;
  agentAdapter?: any;
  preferenceAdapter?: any;
} | null> {
  try {
    // 尝试动态导入 @gongfu/memory
    const memoryModule = await import('@gongfu/memory').catch(() => null);
    
    if (!memoryModule) {
      logger.debug('@gongfu/memory not available');
      return null;
    }
    
    const { MemoryClient, TaskMemoryAdapter, AgentMemoryAdapter, UserPreferenceAdapter } = memoryModule;
    
    // 从环境变量或配置获取必要参数
    const userId = process.env.GONGFU_USER_ID || 'default';
    const projectId = process.env.GONGFU_PROJECT_ID || process.cwd();
    const apiKey = process.env.MEM0_API_KEY;
    
    if (!apiKey) {
      logger.warn('MEM0_API_KEY not set, Memory system will not be available');
      return null;
    }
    
    const config = { userId, projectId, apiKey };
    
    return {
      client: new MemoryClient(config),
      taskAdapter: new TaskMemoryAdapter(config),
      agentAdapter: new AgentMemoryAdapter(config),
      preferenceAdapter: new UserPreferenceAdapter(config)
    };
  } catch (error) {
    logger.error('Failed to load memory adapters:', error);
    return null;
  }
}

/**
 * 创建带有 Memory 集成的 ContextBuilder
 */
export async function createMemoryEnhancedContextBuilder(
  knowledgeBase: KnowledgeBase,
  workflowManager: WorkflowManager,
  taskAnalyzer: TaskAnalyzer,
  options: CreateContextBuilderOptions = {}
): Promise<ContextBuilder> {
  const { contextConfig = {}, memoryConfig, enableMemory = true } = options;
  
  let finalMemoryConfig: MemoryIntegrationConfig | undefined;
  
  // 如果启用 Memory 且没有提供配置，尝试自动加载
  if (enableMemory && !memoryConfig) {
    const adapters = await tryLoadMemoryAdapters();
    if (adapters) {
      finalMemoryConfig = {
        ...adapters,
        enabled: true
      };
      logger.info('Memory system auto-configured');
    }
  } else if (memoryConfig) {
    finalMemoryConfig = memoryConfig;
  }
  
  // 创建 ContextBuilder
  return new ContextBuilder(
    knowledgeBase,
    workflowManager,
    taskAnalyzer,
    {
      ...contextConfig,
      memory: finalMemoryConfig
    }
  );
}

/**
 * 创建基础 ContextBuilder（不带 Memory）
 */
export function createBasicContextBuilder(
  knowledgeBase: KnowledgeBase,
  workflowManager: WorkflowManager,
  taskAnalyzer: TaskAnalyzer,
  config: ContextConfig = {}
): ContextBuilder {
  return new ContextBuilder(
    knowledgeBase,
    workflowManager,
    taskAnalyzer,
    config
  );
}

/**
 * 默认导出：根据环境自动选择
 */
export async function createContextBuilder(
  knowledgeBase: KnowledgeBase,
  workflowManager: WorkflowManager,
  taskAnalyzer: TaskAnalyzer,
  options: CreateContextBuilderOptions = {}
): Promise<ContextBuilder> {
  // 如果明确禁用 Memory，使用基础版本
  if (options.enableMemory === false) {
    return createBasicContextBuilder(
      knowledgeBase,
      workflowManager,
      taskAnalyzer,
      options.contextConfig
    );
  }
  
  // 否则尝试创建增强版本
  return createMemoryEnhancedContextBuilder(
    knowledgeBase,
    workflowManager,
    taskAnalyzer,
    options
  );
}